import { ActivatedRoute, Data, Router } from '@angular/router';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { pagesPATH } from 'src/app/models_new/config/pages';
import {
  ISimulationTableItem,
  SimulationApiService,
} from '../../../services/api/simulation-api.service';
import { IOrganizationContextResolverData } from 'src/app/resolvers/organization-context-resolver.resolver';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { toRequestState } from 'src/app/data-request/operators';
import { DataRequestState } from 'src/app/data-request/model';
import {
  InventoryTableCta,
  InventoryTableDisplayColumn,
  InvetoryTableAction,
} from '../../gui/inventory-table/inventory-table.component';
import {
  FilterTableData,
  ISortingOption,
} from 'src/app/models_new/types/sorting-option';
import { SortDirection } from '@angular/material/sort';
import { ObjectUtils } from 'src/app/utils/object';
import { FilterType, IFilterData } from 'src/app/models_new/types/filter-data';
import { NotificationService } from 'src/app/services/notification.service';
import { IStartSimulation } from 'src/app/services/api/api.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { FileUtils } from 'src/app/utils/file-utils';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-simulations-component',
  templateUrl: './simulations.component.html',
  styleUrls: ['./simulations.component.scss'],
})
export class SimulationsComponent implements OnInit, OnDestroy {
  simulationsItems$: Observable<DataRequestState<ISimulationTableItem[]>>;

  displayedColumns: InventoryTableDisplayColumn[] = [
    { label: 'Name', path: 'nameSlice', sortType: 'string' },
    {
      label: 'Status',
      path: 'simulation_state',
      sortType: 'string',
      specialCell: [
        {
          type: 'badge-n-gauge',
          color: (row: ISimulationTableItem) => {
            if (row.simulation_state === 'Error') {
              return 'warn';
            } else if (row.simulation_state !== 'Successful') {
              return 'primary';
            } else {
              return 'accent';
            }
          },
          paths: ['simulation_status.progress', 'simulation_state'],
        },
      ],
    },
    {
      label: 'Cpm (req.)',
      path: 'inventoryCpmCalc',
      sortType: 'number',
    },
    {
      label: 'Patterns',
      path: 'patterns',
      specialCell: [
        {
          type: 'link',
          link: (e: { name: string; id: string }) =>
            `/${pagesPATH.PATTERNS}/${e.id}`,
        },
      ],
    },
    {
      label: 'Hardware',
      path: 'scene.name',
      sortType: 'string',
      specialCell: [
        {
          type: 'link',
          link: (row) =>
            `/${pagesPATH.INVENTORY}/${pagesPATH.HARDWARE_CONFIGURATIONS}/${row.scene?.id}`,
        },
      ],
    },
    {
      label: 'Software',
      path: 'strategy.name',
      sortType: 'string',
      specialCell: [
        {
          type: 'link',
          link: (row) =>
            `/${pagesPATH.INVENTORY}//${pagesPATH.SOFTWARE_LIST}/${row.strategy?.id}`,
        },
      ],
    },
    {
      label: 'Created At',
      path: 'created_at',
      sortType: 'date',
      initSort: 'desc',
    },
  ];

  rowCta: InventoryTableCta[] = [
    {
      label: 'Repair',
      roleAction: 'play_simulation',
      actionId: 'retry',
      icon: 'build',
      disabled: (row: ISimulationTableItem) => row.simulation_state !== 'Error',
    },
  ];

  rowActions: InvetoryTableAction[] = [
    {
      label: 'Retry',
      actionId: 'retry',
      roleAction: 'play_simulation',
      icon: 'refresh',
      divideAfter: true,
      disabled: (row: ISimulationTableItem) => row.simulation_state === 'Error',
    },
    {
      label: 'Pattern',
      actionId: 'get_pattern_json',
      roleAction: 'download_json_pattern',
      icon: 'download',
      divideAfter: true,
    },
    {
      label: 'Delete',
      actionId: 'delete',
      roleAction: 'delete_simulation',
      color: 'warn',
      icon: 'delete',
    },
  ];

  resultFilter: IFilterData[] = [
    {
      filterType: FilterType.SELECT,
      id: 'simulation_state',
      label: 'Simulation State',
      options: [
        {
          label: 'Queued',
          id: 'Queued',
        },
        {
          label: 'Running',
          id: 'Running',
        },
        {
          label: 'Finished',
          id: 'Finished',
        },
        {
          label: 'Successful',
          id: 'Successful',
        },
        {
          label: 'Error',
          id: 'Error',
        },
        {
          label: 'Cancelled',
          id: 'Cancelled',
        },
        {
          label: 'Initializing',
          id: 'Initializing',
        },
        {
          label: 'Created',
          id: 'Created',
        },
        {
          label: 'Setting up',
          id: 'Setting_up',
        },
        {
          label: 'Starting robot',
          id: 'Starting_robot',
        },
        {
          label: 'Palletizing',
          id: 'Palletizing',
        },
      ],
    },
  ];

  sortingColumns: ISortingOption[] = [
    {
      id: 'nameSlice',
      label: 'Name',
    },
    {
      id: 'pattern.name',
      label: 'Pattern',
    },
    {
      id: 'simulation_state',
      label: 'Status',
    },
    {
      id: 'simulation_status.progress',
      label: 'Progress',
    },
    {
      id: 'simulation_status.calculated.average_pallet_time_formatted',
      label: 'Avg. minutes per pallet',
    },
    {
      id: 'created_at',
      label: 'Created at',
    },
  ];

  blockSelected: ISimulationTableItem[] = [];
  tableFilter: FilterTableData = new FilterTableData();
  orderBy: { column: string; order: SortDirection } = {
    column: 'created_at',
    order: 'desc',
  };

  private destroy$: Subject<boolean> = new Subject<boolean>();
  objUtil = ObjectUtils;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private simulationApi: SimulationApiService,
    private notifier: NotificationService,
    private errorHandler: ErrorHandlerService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.simulationsItems$ = this.route.data.pipe(
      take(1),
      switchMap((data: Data) => {
        const orgId = (data as IOrganizationContextResolverData)
          .organization_id;
        return this.simulationApi.fetchApiSimulationsTable(orgId);
      }),
      switchMap((simulations: ISimulationTableItem[]) => {
        simulations.map((sim: ISimulationTableItem) => {
          const maxNameLength = 30; // Maximum character length
          if (sim.name.length > maxNameLength) {
            sim.nameSlice = sim.name.slice(0, maxNameLength) + '...';
          } else {
            sim.nameSlice = sim.name;
          }

          sim.created_at =
            this.datePipe.transform(sim.created_at, 'dd/MM/yyyy HH:mm') ||
            'N/A';
          // Remove _ from status
          sim.simulation_state = sim.simulation_state.replace(/_/g, ' ') as any;

          // Remove "robot" from status
          sim.simulation_state = sim.simulation_state.replace(
            /robot/gi,
            ''
          ) as any;

          if (sim.simulation_status) {
            if (!sim.simulation_status.progress) {
              sim.simulation_status.progress = '0%';
            } else {
              sim.simulation_status.progress =
                sim.simulation_status.progress + '%';
            }
          }
          sim.patterns = [sim.pattern, sim.second_pattern].filter(
            (pattern) => pattern
          );
          sim.inventoryCpmCalc =
            (sim.simulation_status?.calculated?.cpm || '-') +
            (sim.cpm_requested ? ' (' + sim.cpm_requested + ')' : '');
          return sim;
        });
        return of(simulations);
      }),
      takeUntil(this.destroy$),
      toRequestState()
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public createSimulation(): void {
    this.router.navigate([pagesPATH.SIMULATIONS, pagesPATH.SIMULATION_WIZARD]);
  }

  public onDelete(simulations: ISimulationTableItem[]): void {
    this.notifier
      .deletePrompt(
        'Delete',
        'simulation',
        simulations.map((m) => m.name)
      )
      .afterDismissed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => {
          const operations = simulations
            .map((m) => m.id)
            .map((id) => this.simulationApi.deleteSimulationById(id));
          return forkJoin(operations);
        })
      )
      .subscribe((result) => {
        if (result === null) {
          this.notifier.showError(
            'Delete failed. Are you the owner of this item, and do you have the correct role in your organization?'
          );
        } else {
          this.notifier.showMessage(`The simulation was deleted successfully`);
          this.resetFilters();
        }
      });
  }

  private resetFilters(): void {
    this.tableFilter = new FilterTableData();
  }

  public rowClicked(e: ISimulationTableItem): void {
    this.router.navigate([pagesPATH.SIMULATIONS, e.id]);
  }

  public ctaClicked(e: {
    action: InventoryTableCta;
    row: ISimulationTableItem;
  }): void {
    switch (e.action.actionId) {
      case 'retry':
        this.retrySimulation(e.row);
        break;
    }
  }

  public actionClicked(e: {
    action: InvetoryTableAction;
    row: ISimulationTableItem;
  }): void {
    switch (e.action.actionId) {
      case 'start':
        this.startSimulation(e.row.id);
        break;
      case 'retry':
        this.retrySimulation(e.row);
        break;
      case 'delete':
        this.onDelete([e.row]);
        break;
      case 'get_pattern_json':
        if (e.row.pattern) {
          const pattern = e.row.pattern;
          /**
           * @desc When exporting JSON from MRC, it is advised to remove or replace whitespace in the file name to
           * ensure compatibility with older versions of Pally URCap that cannot handle patterns with whitespace in the file name.
           * {@link https://rocketfarm.atlassian.net/browse/PALLY-3921?focusedCommentId=40602 Learn more} about this issue.
           */
          pattern.data.name = pattern.name = pattern.name.replace(/ /g, '_');
          FileUtils.downloadJson(pattern.data, pattern.name);
        }
        break;
      default:
        console.warn('Action not implemented', e.action.actionId);
        break;
    }
  }

  private retrySimulation(simulation: ISimulationTableItem): void {
    this.router.navigate([
      pagesPATH.SIMULATIONS,
      pagesPATH.SIMULATION_RETRY,
      simulation.id,
    ]);
  }

  private startSimulation(id: string): void {
    this.simulationApi
      .startSimulation(id)
      .pipe(take(1))
      .subscribe((res: IStartSimulation) => {
        if (res && res.simulation_state === 'Queued') {
          this.notifier.showSuccess(res.simulation_state);
        } else {
          this.errorHandler.handleError(
            new Error(
              `Error! Code: ${res.simulation_state}, Message: ${res.simulation_state}`
            )
          );
        }
      });
  }

  public onCompare(elements: ISimulationTableItem[]) {
    const elementIds = elements.map((m) => m.id);
    this.router.navigate([pagesPATH.INVENTORY, pagesPATH.COMPARE_ITEMS], {
      queryParams: {
        ids: elementIds,
        c: 'simulations',
      },
    });
  }
}
